import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getVacancies, deleteVacancy } from "../services/vacancyApi";
import VacancyList from "../components/vacancies/VacancyList";
import VacancyForm from "../components/vacancies/VacancyForm";
import VacancyFilterStepModal from "../components/vacancies/VacancyFilterStepModal";
import CreateVacancyFromRequestModal from "../components/vacancies/CreateVacancyFromRequestModal";
import MainLayout from "../layout/MainLayout";
import { useAlertContext } from "../context/AlertContext";
import { useAuth } from "../context/AuthContext";
import { mapVacancyFromBackend } from "../utils/vacancyMapper";
import HHEmployerVacanciesPanel from "../components/hh/HHEmployerVacanciesPanel";
import HHNegotiationsModal from "../components/hh/HHNegotiationsModal";
import { DEPARTMENTS } from "../config/api";
import { publishHiringRequestToHH } from "../services/hiringRequestsApi";
import { rolesForPath } from "../config/navConfig";
import {
    filterVacancies,
    getVacancyDepartments,
    VACANCY_DEADLINES,
    VACANCY_PLACEMENTS,
} from "../utils/vacancyFilters";

export default function VacanciesPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [vacancies, setVacancies] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editingVacancy, setEditingVacancy] = useState(null);
    const [filterStepVacancy, setFilterStepVacancy] = useState(null);
    const [filterStepAllowSkip, setFilterStepAllowSkip] = useState(true);
    const [showRequestPicker, setShowRequestPicker] = useState(false);
    const [requestVacancyContext, setRequestVacancyContext] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showHidden, setShowHidden] = useState(false);
    const [showArchived, setShowArchived] = useState(false);
    const [vacancyQuery, setVacancyQuery] = useState("");
    const [departmentFilter, setDepartmentFilter] = useState("all");
    const [placementFilter, setPlacementFilter] = useState(VACANCY_PLACEMENTS.ALL);
    const [deadlineFilter, setDeadlineFilter] = useState(VACANCY_DEADLINES.ALL);
    const tab = searchParams.get("tab") === "hh" ? "hh" : "local";
    const setTab = (next) => {
        const params = new URLSearchParams(searchParams);
        if (next === "hh") params.set("tab", "hh");
        else params.delete("tab");
        setSearchParams(params, { replace: true });
    };
    const [negotiationsFor, setNegotiationsFor] = useState(null);
    const { showAlert } = useAlertContext();
    const { user } = useAuth();

    const visibleVacancies = useMemo(() => vacancies.filter((vacancy) => {
        if (!showHidden && vacancy.is_internal_hidden) return false;
        return showArchived ? Boolean(vacancy.archived_at) : !vacancy.archived_at;
    }), [showArchived, showHidden, vacancies]);
    const vacancyDepartments = useMemo(() => getVacancyDepartments(visibleVacancies), [visibleVacancies]);
    const filteredVacancies = useMemo(
        () => filterVacancies(visibleVacancies, {
            query: vacancyQuery,
            department: departmentFilter,
            placement: placementFilter,
            deadline: deadlineFilter,
        }),
        [visibleVacancies, vacancyQuery, departmentFilter, placementFilter, deadlineFilter]
    );
    const hasActiveFilters = Boolean(vacancyQuery.trim())
        || departmentFilter !== "all"
        || placementFilter !== VACANCY_PLACEMENTS.ALL
        || deadlineFilter !== VACANCY_DEADLINES.ALL;

    const resetVacancyFilters = () => {
        setVacancyQuery("");
        setDepartmentFilter("all");
        setPlacementFilter(VACANCY_PLACEMENTS.ALL);
        setDeadlineFilter(VACANCY_DEADLINES.ALL);
    };

    const fetchVacancies = async () => {
        try {
            setLoading(true);
            const department = user?.role === "lead" && user?.department ? user.department : null;
            const data = await getVacancies(department);
            const mappedData = Array.isArray(data) ? data.map((vacancy) => mapVacancyFromBackend(vacancy)) : [];
            const sorted = mappedData.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            setVacancies(sorted);
        } catch (e) {
            console.error("Ошибка загрузки", e);
            showAlert(`Ошибка загрузки вакансий: ${e.message || "Unknown error"}`, "error");
            setVacancies([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user) {
            fetchVacancies();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const handleAddVacancy = () => {
        setRequestVacancyContext(null);
        setEditingVacancy(null);
        setShowForm(true);
    };

    const handleVacancyEdit = (vacancy) => {
        setRequestVacancyContext(
            vacancy?.hiring_request_id ? { requestId: vacancy.hiring_request_id } : null
        );
        setEditingVacancy(vacancy);
        setShowForm(true);
    };

    const handleCreateFromRequest = (vacancy, request) => {
        const mapped = mapVacancyFromBackend(vacancy);
        setVacancies((previous) => previous.some((item) => item.id === mapped.id) ? previous : [mapped, ...previous]);
        setRequestVacancyContext({
            requestId: request.id,
            requestCode: request.public_code || `З-${request.id}`,
        });
        setEditingVacancy(mapped);
        setShowRequestPicker(false);
        setShowForm(true);
        showAlert("Вакансия создана из заявки. Проверьте профессиональную роль, город и описание перед публикацией на HH.ru", "success");
    };

    const handleVacancyDeleted = async (vacancyOrId) => {
        const vacancy =
            typeof vacancyOrId === "object" && vacancyOrId !== null
                ? vacancyOrId
                : vacancies.find((v) => v.id === vacancyOrId);
        const id = vacancy?.id ?? vacancyOrId;

        if (!window.confirm("Удалить вакансию только из HR-платформы? Публикация на HH.ru останется без изменений.")) return;

        if (!id) {
            showAlert("Ошибка: ID вакансии не определен", "error");
            return;
        }

        try {
            await deleteVacancy(id);
            setVacancies((prev) => prev.filter((v) => v.id !== id));
            showAlert("Вакансия удалена из HR-платформы; публикация HH не изменена", "success");
        } catch (e) {
            console.error("Ошибка при удалении", e);
            showAlert(`Ошибка при удалении: ${e.message || "Unknown error"}`, "error");
        }
    };

    const handleFormSuccess = (saved) => {
        const isNewVacancy = !editingVacancy?.id;
        if (saved?.id) {
            setVacancies((prev) => {
                const exists = prev.some((v) => v.id === saved.id);
                return exists
                    ? prev.map((v) => (v.id === saved.id ? saved : v))
                    : [saved, ...prev];
            });
        }
        fetchVacancies();
        setShowForm(false);
        setEditingVacancy(null);
        setRequestVacancyContext(null);
        // After create — offer filter select/create (can be skipped)
        if (isNewVacancy && saved?.id) {
            setFilterStepAllowSkip(true);
            setFilterStepVacancy(saved);
        }
    };

    const handleManageFilter = (vacancy) => {
        setFilterStepAllowSkip(false);
        setFilterStepVacancy(vacancy);
    };

    const handleCloseForm = () => {
        setShowForm(false);
        setEditingVacancy(null);
        setRequestVacancyContext(null);
    };

    const handleFilterStepDone = (updatedVacancy) => {
        if (updatedVacancy?.id) {
            setVacancies((prev) =>
                prev.map((v) => (v.id === updatedVacancy.id ? { ...v, ...updatedVacancy } : v))
            );
        }
        setFilterStepVacancy(null);
        fetchVacancies();
    };

    const handleFilterStepSkip = () => {
        setFilterStepVacancy(null);
    };

    if (loading && tab === "local") {
        return (
            <MainLayout>
                <p>Загрузка...</p>
            </MainLayout>
        );
    }

    return (
        <MainLayout className="p-6">
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight text-slate-900">Вакансии</h1>
                    <p className="mt-1 text-sm text-slate-500">Управление открытыми и архивными вакансиями.</p>
                </div>
                {tab === "local" && (
                    <div className="flex flex-wrap gap-2">
                        <button type="button" onClick={() => setShowRequestPicker(true)} className="flex items-center gap-2 rounded-xl border border-[#d8bb55] bg-[#fffaf0] px-4 py-2.5 text-sm font-bold text-[#8a6b0d] shadow-sm hover:bg-[#fff5d6]" data-testid="create-vacancy-from-request">
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-3-3v6m7 4H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h3l2-2h4l2 2h3a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2Z" /></svg>
                            Из заявки на подбор
                        </button>
                        <button
                            type="button"
                            onClick={handleAddVacancy}
                            className="bg-slate-900 text-white px-4 py-2.5 rounded-xl hover:bg-slate-800 transition-colors shadow-sm text-sm font-medium flex items-center gap-2"
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                            Новая вакансия
                        </button>
                    </div>
                )}
            </div>

            <div className="flex gap-4 mb-6 border-b border-slate-200">
                <button
                    type="button"
                    onClick={() => setTab("local")}
                    className={`pb-3 px-2 text-sm font-medium transition-colors relative ${
                        tab === "local"
                            ? "text-[#cda834]"
                            : "text-slate-500 hover:text-slate-800"
                    }`}
                >
                    В платформе
                    {tab === "local" && (
                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#cda834] rounded-t-full" />
                    )}
                </button>
                <button
                    type="button"
                    onClick={() => setTab("hh")}
                    className={`pb-3 px-2 text-sm font-medium transition-colors relative ${
                        tab === "hh"
                            ? "text-[#cda834]"
                            : "text-slate-500 hover:text-slate-800"
                    }`}
                >
                    С HH.ru
                    {tab === "hh" && (
                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#cda834] rounded-t-full" />
                    )}
                </button>
            </div>

            {tab === "local" ? (
                <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" aria-label="Поиск и фильтры вакансий">
                    <div className="grid gap-3 xl:grid-cols-[minmax(280px,1fr)_220px_220px_220px]">
                        <label className="relative block">
                            <span className="sr-only">Поиск вакансии</span>
                            <svg className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m21 21-4.35-4.35m2.35-5.65a8 8 0 1 1-16 0 8 8 0 0 1 16 0Z" />
                            </svg>
                            <input
                                type="search"
                                value={vacancyQuery}
                                onChange={(event) => setVacancyQuery(event.target.value)}
                                placeholder="Название, код или город"
                                className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 pl-11 pr-4 text-sm text-slate-900 outline-none transition focus:border-[#cda834] focus:bg-white focus:ring-2 focus:ring-[#cda834]/20"
                                data-testid="vacancy-search"
                            />
                        </label>

                        <label>
                            <span className="sr-only">Отдел</span>
                            <select value={departmentFilter} onChange={(event) => setDepartmentFilter(event.target.value)} className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none focus:border-[#cda834] focus:ring-2 focus:ring-[#cda834]/20" data-testid="vacancy-department-filter">
                                <option value="all">Все отделы</option>
                                {vacancyDepartments.map((department) => <option key={department} value={department}>{department}</option>)}
                            </select>
                        </label>

                        <label>
                            <span className="sr-only">Размещение</span>
                            <select value={placementFilter} onChange={(event) => setPlacementFilter(event.target.value)} className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none focus:border-[#cda834] focus:ring-2 focus:ring-[#cda834]/20" data-testid="vacancy-placement-filter">
                                <option value={VACANCY_PLACEMENTS.ALL}>Любое размещение</option>
                                <option value={VACANCY_PLACEMENTS.HH}>Опубликованы на HH.ru</option>
                                <option value={VACANCY_PLACEMENTS.LOCAL}>Только в платформе</option>
                                <option value={VACANCY_PLACEMENTS.TEMPLATE}>Шаблоны</option>
                            </select>
                        </label>

                        <label>
                            <span className="sr-only">Срок закрытия</span>
                            <select value={deadlineFilter} onChange={(event) => setDeadlineFilter(event.target.value)} className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none focus:border-[#cda834] focus:ring-2 focus:ring-[#cda834]/20" data-testid="vacancy-deadline-filter">
                                <option value={VACANCY_DEADLINES.ALL}>Любой срок</option>
                                <option value={VACANCY_DEADLINES.OVERDUE}>Просроченные</option>
                                <option value={VACANCY_DEADLINES.WARNING}>Срок подходит</option>
                                <option value={VACANCY_DEADLINES.ON_TRACK}>Срок в норме</option>
                            </select>
                        </label>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-3">
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                            <span className="text-sm text-slate-500">
                                Найдено: <strong className="font-semibold text-slate-900">{filteredVacancies.length}</strong> из {visibleVacancies.length}
                            </span>
                            <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-600">
                                <input type="checkbox" checked={showHidden} onChange={(event) => setShowHidden(event.target.checked)} className="h-4 w-4 rounded border-slate-300 text-[#cda834] focus:ring-[#cda834]" />
                                Показывать скрытые
                                <span className="text-slate-400">({vacancies.filter((vacancy) => vacancy.is_internal_hidden).length})</span>
                            </label>
                            <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-600">
                                <input type="checkbox" checked={showArchived} onChange={(event) => setShowArchived(event.target.checked)} className="h-4 w-4 rounded border-slate-300 text-[#cda834] focus:ring-[#cda834]" />
                                Архив вакансий
                                <span className="text-slate-400">({vacancies.filter((vacancy) => vacancy.archived_at).length})</span>
                            </label>
                        </div>
                        {hasActiveFilters && (
                            <button type="button" onClick={resetVacancyFilters} className="rounded-lg px-3 py-1.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-100 hover:text-slate-900" data-testid="vacancy-filters-reset">
                                Сбросить фильтры
                            </button>
                        )}
                    </div>
                </section>
            ) : null}

            {tab === "local" ? (
                filteredVacancies.length > 0 || !hasActiveFilters ? (
                <VacancyList
                    vacancies={filteredVacancies}
                    canColdSearch={rolesForPath("/candidates").includes(user?.role)}
                    onVacancyDeleted={handleVacancyDeleted}
                    onVacancyEdit={handleVacancyEdit}
                    onManageFilter={handleManageFilter}
                    onOpenHHNegotiations={(vacancy) =>
                        setNegotiationsFor({
                            id: vacancy.hh_vacancy_id,
                            name: vacancy.name,
                        })
                    }
                    onVacancyUpdated={(updated) => {
                        setVacancies((prev) => {
                            const exists = prev.some((v) => v.id === updated.id);
                            if (exists) {
                                return prev.map((v) => (v.id === updated.id ? { ...v, ...updated } : v));
                            }
                            return [updated, ...prev];
                        });
                    }}
                />
                ) : (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center">
                        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m21 21-4.35-4.35m2.35-5.65a8 8 0 1 1-16 0 8 8 0 0 1 16 0Z" /></svg>
                        </div>
                        <h2 className="text-lg font-bold text-slate-900">Вакансии не найдены</h2>
                        <p className="mt-1 text-sm text-slate-500">Измените запрос или сбросьте выбранные фильтры.</p>
                        <button type="button" onClick={resetVacancyFilters} className="mt-5 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800">
                            Показать все вакансии
                        </button>
                    </div>
                )
            ) : (
                <HHEmployerVacanciesPanel
                    localVacancies={vacancies}
                    onLinked={fetchVacancies}
                    defaultDepartment={
                        user?.department && DEPARTMENTS.includes(user.department)
                            ? user.department
                            : undefined
                    }
                />
            )}

            {negotiationsFor?.id && (
                <HHNegotiationsModal
                    hhVacancyId={String(negotiationsFor.id)}
                    vacancyName={negotiationsFor.name}
                    onClose={() => setNegotiationsFor(null)}
                />
            )}

            {showForm && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex justify-center items-start z-50 overflow-y-auto py-8 px-4">
                    <div className="bg-white p-6 rounded-2xl shadow-xl w-full max-w-6xl ring-1 ring-slate-900/5">
                        {requestVacancyContext?.requestId && (
                            <div className="mb-5 rounded-2xl border border-[#e8d58b] bg-[#fffaf0] px-4 py-3 text-sm text-slate-700">
                                <p className="font-bold text-slate-900">Вакансия создана из заявки {requestVacancyContext.requestCode || `З-${requestVacancyContext.requestId}`}</p>
                                <p className="mt-1 text-xs leading-5 text-slate-600">После публикации на HH.ru заявка автоматически перейдёт в статус «Опубликована». Заполните профессиональную роль, город и проверьте описание.</p>
                            </div>
                        )}
                        <VacancyForm
                            initialData={editingVacancy}
                            onClose={handleCloseForm}
                            onVacancyAdded={handleFormSuccess}
                            publishToHH={requestVacancyContext?.requestId
                                ? () => publishHiringRequestToHH(requestVacancyContext.requestId)
                                : null}
                        />
                    </div>
                </div>
            )}

            {filterStepVacancy?.id && (
                <VacancyFilterStepModal
                    vacancy={filterStepVacancy}
                    allowSkip={filterStepAllowSkip}
                    onDone={handleFilterStepDone}
                    onSkip={handleFilterStepSkip}
                />
            )}

            {showRequestPicker && (
                <CreateVacancyFromRequestModal
                    onClose={() => setShowRequestPicker(false)}
                    onCreated={handleCreateFromRequest}
                />
            )}
        </MainLayout>
    );
}
